import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { ScanBar } from '../../../../../../generic/ui/Input';
import { Mono } from '../../../../../../generic/ui/Mono';
import PackageBoxIcon from '../../../../../../generic/icons/IconLib/PackageBoxIcon';
import SproutIcon from '../../../../../../generic/icons/IconLib/SproutIcon';
import { useDetectOutsideClick } from '../../../../../../generic/hooks/useDetectOutsideClick';
import { ISrikaItem } from '../../../../../../modules/API/Gathering/models/srika';

type BarcodeScannerPanelProps = {
    input: string;
    setInput: (value: string) => void;
    onScan: (barcode?: string) => void | Promise<void>;
    onSelectFromList: (item: ISrikaItem) => void;
    barcodeList?: ISrikaItem[];
    autoFocus?: boolean;
    disabled?: boolean;
    placeholder?: string;
    tabIndex?: number;
};

const BarcodeScannerPanel: React.FC<BarcodeScannerPanelProps> = ({
    input,
    setInput,
    onScan,
    onSelectFromList,
    barcodeList,
    autoFocus = true,
    disabled = false,
    placeholder = 'סריקה / חיפוש',
    tabIndex,
}) => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const [isOpen, setIsOpen] = useState<boolean>(false);

    const hasBarcodeList = !!barcodeList && barcodeList.length > 0;

    useEffect(() => {
        if (!autoFocus) return;
        inputRef.current?.focus();
    }, [autoFocus]);

    useDetectOutsideClick(wrapperRef, () => setIsOpen(false));

    const handleScan = async (barcode?: string) => {
        if (disabled) return;
        await onScan(barcode);
        setIsOpen(false);
        inputRef.current?.blur();
    };

    const handleSelectBarcode = (item: ISrikaItem) => {
        if (disabled) return;
        onSelectFromList(item);
        setIsOpen(false);
    };

    const handleFocus = () => {
        if (hasBarcodeList) setIsOpen(true);
    };

    return (
        <Wrapper ref={wrapperRef} onFocus={handleFocus}>
            <ScanBar
                ref={inputRef}
                value={input}
                onChange={setInput}
                onScan={handleScan}
                placeholder={placeholder}
                disabled={disabled}
                tabIndex={tabIndex}
            />
            {isOpen && hasBarcodeList && (
                <DropdownPanel>
                    {barcodeList!.map((item) => (
                        <DropdownItem
                            key={item.BarcodeID}
                            type='button'
                            onClick={() => handleSelectBarcode(item)}
                        >
                            <ItemLine>
                                <ItemIcon>
                                    <SproutIcon />
                                </ItemIcon>
                                <ItemPrimary>
                                    {`${item.ProductDesc?.trim() ?? ''} ${item.VarietyDesc?.trim() ?? ''} ${item.SizeDesc?.trim() ?? ''}`.trim()}
                                </ItemPrimary>
                            </ItemLine>
                            {item.Pack_Name && (
                                <ItemLine>
                                    <ItemIcon $muted>
                                        <PackageBoxIcon />
                                    </ItemIcon>
                                    <ItemSecondary>
                                        {item.Pack_Name.trim()}
                                    </ItemSecondary>
                                </ItemLine>
                            )}
                            {item.BarcodeID != null && (
                                <ItemMeta>
                                    <Mono>{item.BarcodeID}</Mono>
                                </ItemMeta>
                            )}
                        </DropdownItem>
                    ))}
                </DropdownPanel>
            )}
        </Wrapper>
    );
};

export default BarcodeScannerPanel;

const Wrapper = styled.div`
    position: relative;
    width: 100%;
`;

const DropdownPanel = styled.div`
    position: absolute;
    top: calc(100% + 0.375rem);
    inset-inline-start: 0;
    inset-inline-end: 0;
    z-index: 20;
    max-height: 18rem;
    overflow-y: auto;
    background: ${({ theme }) => theme.colors.surface};
    border: 1px solid ${({ theme }) => theme.colors.line};
    border-radius: ${({ theme }) => theme.radii.md};
    box-shadow: ${({ theme }) => theme.shadows.card};
    padding: 0.25rem;
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    direction: rtl;

    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
        width: 0;
        height: 0;
    }
`;

const DropdownItem = styled.button`
    appearance: none;
    width: 100%;
    border: 0;
    background: transparent;
    text-align: start;
    padding: 0.5rem 0.625rem;
    border-radius: ${({ theme }) => theme.radii.sm};
    cursor: pointer;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    color: ${({ theme }) => theme.colors.ink};
    transition: background ${({ theme }) => theme.motion.fast} ease;

    &:hover,
    &:focus-visible {
        background: ${({ theme }) => theme.colors.line2};
        outline: none;
    }
`;

const ItemLine = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
`;

const ItemIcon = styled.span<{ $muted?: boolean }>`
    display: inline-flex;
    align-items: center;
    color: ${({ theme, $muted }) =>
        $muted ? theme.colors.ink3 : theme.colors.ink2};
    flex-shrink: 0;

    svg {
        width: 0.875rem;
        height: 0.875rem;
    }
`;

const ItemPrimary = styled.span`
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.md};
    font-weight: 500;
    color: ${({ theme }) => theme.colors.ink};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
`;

const ItemSecondary = styled.span`
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.sm};
    color: ${({ theme }) => theme.colors.ink3};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const ItemMeta = styled.span`
    font-size: ${({ theme }) => theme.text.xs};
    color: ${({ theme }) => theme.colors.ink4};
    align-self: flex-end;
`;
